"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CommissioningDefaultsWrite = exports.CommissioningAssessment = exports.CommissioningCommand = exports.CommissioningReadinessEvidence = exports.ReadinessScenarioNames = exports.CommissioningState = void 0;
exports.assessCommissioningReadiness = assessCommissioningReadiness;
exports.commissioningStatus = commissioningStatus;
const zod_1 = require("zod");
const Id = zod_1.z.string().trim().min(1).max(128);
const Revision = zod_1.z.string().regex(/^(0|[1-9]\d*)$/);
const Instant = zod_1.z.string().datetime();
const Ratio = zod_1.z.number().finite().min(0).max(1);
exports.CommissioningState = zod_1.z
    .object({
    revision: Revision,
    mode: zod_1.z.enum(['learning', 'enforcement']),
    startedAt: Instant,
    expiresAt: Instant,
    activatedAt: Instant.nullable(),
})
    .strict()
    .superRefine((s, ctx) => {
    if (Date.parse(s.expiresAt) <= Date.parse(s.startedAt))
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['expiresAt'],
            message: 'Expiry must follow start',
        });
    if ((s.mode === 'learning') !== (s.activatedAt === null))
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['activatedAt'],
            message: 'Activation must match enforcement state',
        });
});
exports.ReadinessScenarioNames = [
    'substitution',
    'unscanned_addition',
    'removal_void',
    'quantity_change',
    'customer_bag_tare',
    'skip_bagging',
    'sold_by_weight',
    'payment_hold',
    'scale_fault_recovery',
    'audit_outcome',
    'four_day_outage',
    'link_lan_outage',
    'restart',
    'routine_storage_pressure',
    'duplicate_reconnect',
];
exports.CommissioningReadinessEvidence = zod_1.z
    .object({
    tenantId: Id,
    storeId: Id,
    approvedLimits: zod_1.z
        .object({
        approvedBy: Id,
        approvedAt: Instant,
        minProtectedCoverage: Ratio,
        minTrustedCoverage: Ratio,
        maxFalseInterventionRate: Ratio,
        maxAuditsPerHour: zod_1.z.number().finite().nonnegative(),
    })
        .strict(),
    metrics: zod_1.z
        .object({
        protectedCoverage: Ratio,
        trustedCoverage: Ratio,
        falseInterventionRate: Ratio,
        auditsPerHour: zod_1.z.number().finite().nonnegative(),
    })
        .strict(),
    evidenceDenominators: zod_1.z
        .object({
        protectedCoverage: zod_1.z.number().int().safe().positive(),
        trustedCoverage: zod_1.z.number().int().safe().positive(),
        falseInterventionRate: zod_1.z.number().int().safe().positive(),
        auditsPerHour: zod_1.z.number().finite().positive(),
    })
        .strict(),
    measuredFrom: Instant,
    measuredTo: Instant,
    unresolvedCriticalInvestigations: zod_1.z.number().int().safe().nonnegative(),
    physicalScenarios: zod_1.z.record(zod_1.z.enum(exports.ReadinessScenarioNames), zod_1.z
        .object({
        status: zod_1.z.enum(['passed', 'failed', 'not_tested']),
        artifact: zod_1.z.string().trim().min(1).max(2048).optional(),
        verifiedBy: Id.optional(),
    })
        .strict()),
})
    .strict()
    .refine((e) => Date.parse(e.measuredTo) > Date.parse(e.measuredFrom), {
    message: 'Measurement window must be positive',
    path: ['measuredTo'],
});
function assessCommissioningReadiness(e) {
    const problems = [];
    for (const [metric, bound, minimum] of [
        ['protectedCoverage', 'minProtectedCoverage', true],
        ['trustedCoverage', 'minTrustedCoverage', true],
        ['falseInterventionRate', 'maxFalseInterventionRate', false],
        ['auditsPerHour', 'maxAuditsPerHour', false],
    ]) {
        if (minimum
            ? e.metrics[metric] < e.approvedLimits[bound]
            : e.metrics[metric] > e.approvedLimits[bound])
            problems.push(`${metric}: approved limit failed`);
    }
    if (e.unresolvedCriticalInvestigations !== 0)
        problems.push('critical source/item investigations must be resolved');
    for (const name of exports.ReadinessScenarioNames) {
        const v = e.physicalScenarios[name];
        if (!v || v.status !== 'passed' || !v.artifact || !v.verifiedBy)
            problems.push(`${name}: independently verified physical evidence required`);
    }
    return {
        eligible: problems.length === 0,
        problems,
        claim: 'Evidence references require authorized human verification; no causal theft-reduction claim',
    };
}
const Base = zod_1.z.object({
    commandId: zod_1.z.string().uuid(),
    storeId: Id,
    expectedRevision: Revision,
    reason: zod_1.z.string().trim().min(1).max(2000),
});
exports.CommissioningCommand = zod_1.z.discriminatedUnion('action', [
    Base.extend({
        action: zod_1.z.literal('start'),
        durationDays: zod_1.z.number().int().safe().positive().optional(),
    }).strict(),
    Base.extend({
        action: zod_1.z.literal('extend'),
        durationDays: zod_1.z.number().int().safe().positive(),
    }).strict(),
    Base.extend({
        action: zod_1.z.literal('activate'),
        readiness: exports.CommissioningReadinessEvidence,
    }).strict(),
]);
exports.CommissioningAssessment = zod_1.z
    .object({
    storeId: Id,
    expectedRevision: Revision,
    readiness: exports.CommissioningReadinessEvidence,
})
    .strict();
function commissioningStatus(state, now) {
    return state === null
        ? 'unconfigured'
        : state.mode === 'enforcement'
            ? 'enforcement'
            : now.getTime() >= Date.parse(state.expiresAt)
                ? 'overdue'
                : 'learning';
}
exports.CommissioningDefaultsWrite = zod_1.z
    .object({
    commandId: zod_1.z.string().uuid(),
    expectedRevision: Revision,
    reason: zod_1.z.string().trim().min(1).max(2000),
    defaultDurationDays: zod_1.z.number().int().safe().positive(),
})
    .strict();
//# sourceMappingURL=securescale-commissioning.js.map